import { useEffect, useRef, useState } from 'react';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import { formatarNascimento, mascaraCPF, mascaraTelefone } from '@/pages/inscricao/utils';
import type { FeedbackCampo, PerguntaEditar } from '../types';

interface Props {
  pergunta: PerguntaEditar;
  editando: boolean;
  valor: string;
  feedback: FeedbackCampo | null;
  salvando: boolean;
  aoSalvar: (novo: string) => void;
}

// Tipos que viram texto e voltam sem perder informação pelo `salvar_resposta`.
const EDITAVEIS = new Set([
  'texto_curto',
  'texto_longo',
  'numero',
  'data',
  'telefone',
  'email',
  'cpf',
  'sim_nao',
  'escolha_unica',
]);

function tentarParseLista(valor: string): string[] {
  if (!valor) return [];
  try {
    const p = JSON.parse(valor);
    if (Array.isArray(p)) return p.map((x) => (typeof x === 'string' ? x : JSON.stringify(x)));
  } catch {
    // não é json: trata como texto simples
  }
  return [valor];
}

function textoResposta(p: PerguntaEditar, valor: string): string {
  if (!valor) return '';
  if (p.tipo === 'sim_nao') return valor === 'true' ? 'Sim' : 'Não';
  if (p.tipo === 'multipla_escolha' || p.tipo === 'lista') {
    return tentarParseLista(valor).join(', ');
  }
  if (p.tipo === 'data') return formatarNascimento(valor);
  if (p.tipo === 'telefone') return mascaraTelefone(valor);
  if (p.tipo === 'cpf') return mascaraCPF(valor);
  return valor;
}

const ESTILO_CAMPO =
  'mt-1 w-full border-0 border-b border-secondary-300 bg-transparent px-0 py-2 text-[15px] text-foreground-950 outline-none transition-colors placeholder:text-secondary-400 focus:border-b-2 focus:border-primary-500';

export default function CampoEdicao({
  pergunta: p,
  editando,
  valor,
  feedback,
  salvando,
  aoSalvar,
}: Props) {
  const [local, setLocal] = useState(valor);
  const caixaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocal(valor);
  }, [valor]);

  // Senha da fila repetida (ou inválida): rola até o campo e põe o foco nele.
  useEffect(() => {
    if (p.chave !== 'senha_fila') return;
    if (!feedback || feedback.tipo !== 'erro') return;
    const caixa = caixaRef.current;
    if (!caixa) return;
    caixa.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const campo = caixa.querySelector<HTMLElement>('input, textarea');
    const timer = window.setTimeout(() => campo?.focus({ preventScroll: true }), 320);
    return () => window.clearTimeout(timer);
  }, [feedback, p.chave]);

  const podeEditar = editando && EDITAVEIS.has(p.tipo);

  // ————— Modo leitura —————
  if (!podeEditar) {
    return (
      <div>
        {p.tipo === 'foto' ? (
          <div className="mt-1 flex items-center gap-2 text-[15px] text-foreground-800">
            <span className="flex h-5 w-5 items-center justify-center text-green-600">
              <i className={valor ? 'ri-checkbox-circle-fill' : 'ri-error-warning-line'} />
            </span>
            {valor ? 'Foto enviada' : 'Sem foto'}
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-[15px] text-foreground-800">
            {textoResposta(p, valor) || <span className="text-secondary-400">Sem resposta</span>}
          </p>
        )}
        {!editando && p.tipo !== 'foto' && (
          <p className="mt-1 text-xs text-secondary-400">Somente leitura</p>
        )}
        {editando && (
          <p className="mt-1 text-xs text-secondary-400">
            Este campo não pode ser alterado por aqui.
          </p>
        )}
      </div>
    );
  }

  // ————— Modo edição —————
  const salvarDiscreto = (novo: string) => {
    setLocal(novo);
    if (novo !== valor) aoSalvar(novo);
  };

  const aoDesfocar = () => {
    if (local !== valor) aoSalvar(local);
  };

  return (
    <div ref={caixaRef}>
      {p.tipo === 'texto_longo' ? (
        <textarea
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={aoDesfocar}
          rows={4}
          placeholder="Sua resposta"
          className={`${ESTILO_CAMPO} resize-y border`}
        />
      ) : p.tipo === 'numero' ? (
        <input
          type="number"
          inputMode="numeric"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={aoDesfocar}
          placeholder="Sua resposta"
          className={ESTILO_CAMPO}
        />
      ) : p.tipo === 'sim_nao' ? (
        <div className="mt-1 space-y-1">
          <OpcaoSelecao tipo="radio" checked={local === 'true'} onChange={() => salvarDiscreto('true')}>
            Sim
          </OpcaoSelecao>
          <OpcaoSelecao
            tipo="radio"
            checked={local === 'false'}
            onChange={() => salvarDiscreto('false')}
          >
            Não
          </OpcaoSelecao>
        </div>
      ) : p.tipo === 'escolha_unica' ? (
        <div className="mt-1 space-y-1">
          {(p.opcoes ?? []).map((opcao) => (
            <OpcaoSelecao
              key={opcao}
              tipo="radio"
              checked={local === opcao}
              onChange={() => salvarDiscreto(opcao)}
            >
              {opcao}
            </OpcaoSelecao>
          ))}
        </div>
      ) : (
        <input
          type={p.tipo === 'email' ? 'email' : 'text'}
          inputMode={
            p.tipo === 'email'
              ? 'email'
              : p.tipo === 'telefone' || p.tipo === 'cpf'
                ? 'numeric'
                : 'text'
          }
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={aoDesfocar}
          placeholder="Sua resposta"
          className={ESTILO_CAMPO}
        />
      )}

      {salvando && (
        <p className="mt-1 text-xs text-secondary-400">Salvando…</p>
      )}

      {feedback && !salvando && (
        <p
          className={`mt-1 text-sm ${feedback.tipo === 'erro' ? 'text-danger-600' : 'text-green-600'}`}
          role="status"
        >
          {feedback.texto}
        </p>
      )}
    </div>
  );
}